import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import YhButton from "../../../../ui/YhButton";
import urls from "../../../../global/constants/UrlConstants";
import DeleteAccountImg from "../../../../assets/images/delete-account.svg";
import { selectActiveOrganizationId } from "../../../../redux/authSlice";
import {
  getCrmLeadPreferences,
  updateCrmLeadPreferences,
} from "../../../../services/organizationService";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../../../DashBoard/DashboardService";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAccount?: Function;
}

type PrefSection =
  | "lead"
  | "automation"
  | "pipeline"
  | "notifications"
  | "account";

type NotifPrefKey = "stageChange" | "assignment" | "reminder" | "teamAlert";

type NotifPrefs = Record<NotifPrefKey, boolean>;

const SECTIONS: { id: PrefSection; label: string; hint: string }[] = [
  {
    id: "lead",
    label: "Lead Settings",
    hint: "How assigned leads work for team members",
  },
  {
    id: "automation",
    label: "Automation",
    hint: "Lead assign workflows (global / project / form)",
  },
  {
    id: "pipeline",
    label: "Pipeline Settings",
    hint: "Stage behaviour across All / My / Team leads",
  },
  {
    id: "notifications",
    label: "Notifications",
    hint: "Choose what alerts you want to receive",
  },
  {
    id: "account",
    label: "Account Status",
    hint: "Keep active or request deletion",
  },
];

const DEFAULT_CRM_PREFS = {
  leadAssignmentMode: "separate" as "separate" | "shared",
};

const DEFAULT_NOTIF_PREFS: NotifPrefs = {
  stageChange: true,
  assignment: true,
  reminder: true,
  teamAlert: true,
};

const NOTIF_TOGGLES: {
  key: NotifPrefKey;
  label: string;
  hint: string;
}[] = [
  {
    key: "assignment",
    label: "Lead assignments",
    hint: "When a lead is assigned or reassigned to you",
  },
  {
    key: "stageChange",
    label: "Stage changes (close / won / lost)",
    hint: "When a lead is closed, won, or marked not interested (for TL / admin)",
  },
  {
    key: "reminder",
    label: "Reminders",
    hint: "Your follow-up reminders and overdue alerts",
  },
  {
    key: "teamAlert",
    label: "Team alerts",
    hint: "Team activity, notes, and member updates",
  },
];

const Toggle = ({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
      checked ? "bg-[#A02448]" : "bg-[#D1D5DB]"
    } ${disabled ? "opacity-50" : ""}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const AccountStatus = (props: CustomProps = {}) => {
  const history = useHistory();
  const isEmbedded = props.isDialogOpen !== undefined;
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const [section, setSection] = useState<PrefSection>("lead");
  const [selectedValue, setSelectedValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CRM_PREFS);
  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs>(DEFAULT_NOTIF_PREFS);
  const [loadingNotif, setLoadingNotif] = useState(false);

  const handleClose = () => {
    if (props.handleCloseAccount) {
      props.handleCloseAccount();
      return;
    }
    history.push(urls.accountSettingsViewPth);
  };

  useEffect(() => {
    const orgId = String(activeOrganizationId || "").trim();
    if (!orgId || orgId === "all") return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getCrmLeadPreferences(orgId);
        const data = res?.data || res;
        if (!cancelled && data) {
          setPrefs({
            leadAssignmentMode:
              data.leadAssignmentMode === "shared" ? "shared" : "separate",
          });
        }
      } catch (err: any) {
        if (!cancelled) {
          openErrorNotification(
            isTruthy(err?.message) ? err.message : notifiers.GENERIC_ERROR
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrganizationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingNotif(true);
        const res = await getNotificationPreferences();
        const loaded = res?.data?.prefs || res?.prefs || res?.data || {};
        if (!cancelled) {
          setNotifPrefs({ ...DEFAULT_NOTIF_PREFS, ...loaded });
        }
      } catch (_) {
        if (!cancelled) setNotifPrefs(DEFAULT_NOTIF_PREFS);
      } finally {
        if (!cancelled) setLoadingNotif(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const savePrefs = async (next = prefs) => {
    const orgId = String(activeOrganizationId || "").trim();
    if (!orgId || orgId === "all") {
      openErrorNotification("Select a specific organization first.");
      return;
    }
    try {
      setSaving(true);
      const res = await updateCrmLeadPreferences(orgId, next);
      const data = res?.data || res || next;
      setPrefs({
        leadAssignmentMode:
          data.leadAssignmentMode === "shared" ? "shared" : "separate",
      });
      openSuccessNotification("CRM preferences saved");
    } catch (err: any) {
      openErrorNotification(
        isTruthy(err?.message) ? err.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const saveNotifPrefs = async (next = notifPrefs) => {
    try {
      setSavingNotif(true);
      const res = await updateNotificationPreferences(next);
      const loaded = res?.data?.prefs || res?.prefs || next;
      setNotifPrefs({ ...DEFAULT_NOTIF_PREFS, ...loaded });
      openSuccessNotification("Notification preferences saved");
    } catch (err: any) {
      openErrorNotification(
        isTruthy(err?.message) ? err.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSavingNotif(false);
    }
  };

  const toggleNotif = (key: NotifPrefKey, value: boolean) => {
    setNotifPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(90vh,720px)] w-full max-w-4xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B]">
            CRM Preferences
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Customize CRM experience as per your needs.
          </p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Close"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden pb-20">
        <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[220px_1fr]">
          <nav className="flex shrink-0 flex-col gap-1 overflow-auto rounded-2xl border border-[#EEF0F5] bg-[#FAFBFC] p-2">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className={`rounded-xl px-3 py-2.5 text-left transition ${
                  section === s.id
                    ? "bg-white text-[#A02448] shadow-sm ring-1 ring-[#F3D5DF]"
                    : "text-[#4B5563] hover:bg-white/80"
                }`}
              >
                <div className="text-sm font-semibold">{s.label}</div>
                <div className="mt-0.5 text-[11px] leading-snug text-[#9CA3AF]">
                  {s.hint}
                </div>
              </button>
            ))}
          </nav>

          <section className="min-h-0 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
            {loading && section !== "notifications" ? (
              <p className="text-sm text-[#6B7280]">Loading preferences…</p>
            ) : null}

            {section === "lead" ? (
              <div className="flex max-w-2xl flex-col gap-4">
                <div>
                  <p className="text-sm font-semibold text-[#111827]">
                    Lead assignment mode
                  </p>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    When a lead is assigned to multiple team members, choose how
                    each person works that lead. Default is{" "}
                    <strong>Separate</strong> — used across All Leads, My Leads
                    and Team Leads.
                  </p>
                </div>

                <label
                  className={`flex cursor-pointer gap-3 rounded-xl border px-4 py-3 text-sm ${
                    prefs.leadAssignmentMode === "separate"
                      ? "border-[#A02448] bg-[#FDF5F7]"
                      : "border-[#EEF0F5]"
                  }`}
                >
                  <input
                    type="radio"
                    name="leadAssignmentMode"
                    className="mt-1 h-4 w-4 accent-[#A02448]"
                    checked={prefs.leadAssignmentMode === "separate"}
                    onChange={() =>
                      setPrefs((p) => ({
                        ...p,
                        leadAssignmentMode: "separate",
                      }))
                    }
                  />
                  <span>
                    <span className="font-semibold text-[#111827]">
                      Separate (recommended)
                    </span>
                    <span className="mt-1 block text-[#6B7280]">
                      Same lead, but User A and User B manage their own stage.
                      If A marks Interested, B still sees Initial / New until B
                      updates. Status is private per assignee.
                    </span>
                  </span>
                </label>

                <label
                  className={`flex cursor-pointer gap-3 rounded-xl border px-4 py-3 text-sm ${
                    prefs.leadAssignmentMode === "shared"
                      ? "border-[#A02448] bg-[#FDF5F7]"
                      : "border-[#EEF0F5]"
                  }`}
                >
                  <input
                    type="radio"
                    name="leadAssignmentMode"
                    className="mt-1 h-4 w-4 accent-[#A02448]"
                    checked={prefs.leadAssignmentMode === "shared"}
                    onChange={() =>
                      setPrefs((p) => ({
                        ...p,
                        leadAssignmentMode: "shared",
                      }))
                    }
                  />
                  <span>
                    <span className="font-semibold text-[#111827]">
                      Shared (single lead)
                    </span>
                    <span className="mt-1 block text-[#6B7280]">
                      One stage for everyone. If User A marks Not Interested,
                      User B also sees Not Interested.
                    </span>
                  </span>
                </label>

                <div className="flex justify-end">
                  <YhButton
                    variant="primary"
                    size="sm"
                    disabled={saving}
                    onClick={() => savePrefs()}
                  >
                    {saving ? "Saving…" : "Save lead settings"}
                  </YhButton>
                </div>
              </div>
            ) : null}

            {section === "automation" ? (
              <div className="flex max-w-xl flex-col gap-3">
                <p className="text-sm font-semibold text-[#111827]">
                  Automation lead flow
                </p>
                <p className="text-sm text-[#6B7280]">
                  Create global, organization, project-wise or form-wise assign
                  workflows. If an organization has no published assign flow,
                  the global workflow is used.
                </p>
                <YhButton
                  variant="primary"
                  size="sm"
                  onClick={() => history.push(urls.automationLeadFlowPath)}
                >
                  Open Automation Lead Flow
                </YhButton>
              </div>
            ) : null}

            {section === "pipeline" ? (
              <div className="flex max-w-xl flex-col gap-3">
                <p className="text-sm font-semibold text-[#111827]">
                  Pipeline behaviour
                </p>
                <p className="text-sm text-[#6B7280]">
                  With <strong>Separate</strong> mode, stage filters on All /
                  My / Team Leads use each assignee&apos;s own stage. Peer stage
                  changes do not overwrite another user&apos;s view.
                </p>
                <p className="rounded-xl bg-[#F9FAFB] px-3 py-2 text-xs text-[#6B7280]">
                  Current mode:{" "}
                  <strong className="capitalize text-[#111827]">
                    {prefs.leadAssignmentMode}
                  </strong>
                </p>
              </div>
            ) : null}

            {section === "notifications" ? (
              <div className="flex max-w-xl flex-col gap-4">
                <div>
                  <p className="text-sm font-semibold text-[#111827]">
                    Notification preferences
                  </p>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    Enable or disable the alerts you want to receive on this
                    account. Changes apply to push and in-app notifications.
                  </p>
                </div>

                {loadingNotif ? (
                  <p className="text-sm text-[#6B7280]">Loading…</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {NOTIF_TOGGLES.map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between gap-4 rounded-xl border border-[#EEF0F5] px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#111827]">
                            {item.label}
                          </p>
                          <p className="mt-0.5 text-xs text-[#6B7280]">
                            {item.hint}
                          </p>
                        </div>
                        <Toggle
                          checked={Boolean(notifPrefs[item.key])}
                          disabled={savingNotif}
                          onChange={(next) => toggleNotif(item.key, next)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    className="text-xs font-semibold text-[#6B7280] hover:text-[#A02448]"
                    disabled={savingNotif}
                    onClick={() => setNotifPrefs(DEFAULT_NOTIF_PREFS)}
                  >
                    Reset to all on
                  </button>
                  <YhButton
                    variant="primary"
                    size="sm"
                    disabled={savingNotif || loadingNotif}
                    onClick={() => saveNotifPrefs()}
                  >
                    {savingNotif ? "Saving…" : "Save notification settings"}
                  </YhButton>
                </div>
              </div>
            ) : null}

            {section === "account" ? (
              !confirmDelete ? (
                <div className="flex max-w-lg flex-col gap-4">
                  <p className="text-sm font-semibold text-[#111827]">
                    Account Status
                  </p>
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#EEF0F5] px-4 py-3 text-sm text-[#374151]">
                    <input
                      type="radio"
                      name="accountStatus"
                      value="active"
                      checked={selectedValue === "active"}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      className="h-4 w-4 accent-[#A02448]"
                    />
                    Keep account active
                  </label>
                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#EEF0F5] px-4 py-3 text-sm text-[#374151]">
                    <input
                      type="radio"
                      name="accountStatus"
                      value="delete"
                      checked={selectedValue === "delete"}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      className="h-4 w-4 accent-[#A02448]"
                    />
                    Request account deletion
                  </label>
                  {selectedValue === "delete" ? (
                    <YhButton
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmDelete(true)}
                    >
                      Continue
                    </YhButton>
                  ) : null}
                </div>
              ) : (
                <div className="flex max-w-md flex-col items-center text-center">
                  <img
                    src={DeleteAccountImg}
                    alt=""
                    className="mb-4 h-28 w-auto"
                  />
                  <h2 className="text-lg font-bold text-[#111827]">
                    Are you sure?
                  </h2>
                  <p className="mt-2 text-sm text-[#6B7280]">
                    Please take a look before deleting your account. If you are
                    sure, click Delete.
                  </p>
                  <div className="mt-5 flex gap-2">
                    <YhButton
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Delete
                    </YhButton>
                    <YhButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </YhButton>
                  </div>
                </div>
              )
            ) : null}
          </section>
        </div>
      </div>

      <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
          aria-label="Back"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <YhButton variant="secondary" size="sm" onClick={handleClose}>
          Back
        </YhButton>
      </div>
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-4xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default AccountStatus;
